<template>
  <div class="accordion" :class="{ open: open }" :style="{
      '--icon-mask': `url(${open && iconMaskOpen ? iconMaskOpen : iconMask})`,
      '--icon-color': iconColor,
      '--icon-width': iconWidth,
      '--icon-height': iconHeight,
    }">
    <div class="header-part d-flex justify-content-between align-items-center" @click="toggleContent">
      <div class="title">
        <slot name="header"></slot>
      </div>
      <div class="toggle-icon">
        <span
          :class="[{ open: open && rotateIconOnOpen }, iconClass]"
          class="toggle-arrow"
        />
        <!-- <i
          class="toggle-arrow bb-arrow-down"
          :class="[{ open: open }, iconClass]"
        ></i> -->
      </div>
    </div>
    <div class="content-part" ref="content-part" v-if="contentIsOpened">
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: "Accordion",
  props: {
    maxHeight: {
      type: [Number, String],
      default: 340,
    },
    iconClass: {
      type: String,
    },
    iconMask: {
      type: String,
      default: "/images/icons/bb-icon_arrow-down.svg",
    },
    // swapped in while the panel is open (plus -> minus)
    iconMaskOpen: {
      type: String,
      default: "",
    },
    iconColor: {
      type: String,
      default: "#6C727F",
    },
    iconWidth: {
      type: String,
      default: "20px",
    },
    iconHeight: {
      type: String,
      default: "10px",
    },
    // the arrow flips when open; a plus/cross style icon should not
    rotateIconOnOpen: {
      type: Boolean,
      default: true,
    },
  },
  components: {},
  data: () => ({
    contentIsOpened: false,
    open: false,
    animation: null,
  }),
  created() {},
  computed: {},
  methods: {
    // `open` is the single source of truth: `contentIsOpened` only controls
    // whether the panel is in the DOM, and it lags behind by the close
    // animation. Deciding on `contentIsOpened` meant a click landing mid-close
    // ran closeContent() again while the icon had already flipped to minus.
    toggleContent() {
      if (this.open) {
        this.closeContent();
      } else {
        this.openContent();
      }
    },
    openContent() {
      this.open = true;
      this.contentIsOpened = true;
      this.$nextTick(() => {
        const el = this.$refs["content-part"];
        if (!el) return;
        this.stopAnimation();
        // Animate to the content's own height, from wherever it currently sits
        // (0 when freshly mounted, part-way when a close was interrupted).
        // Easing to a fixed 340px meant the panel was fully revealed in the
        // first few frames, which read as no animation at all.
        this.animation = this.$anime({
          targets: el,
          maxHeight: [el.offsetHeight, el.scrollHeight],
          easing: "easeOutQuart",
          duration: 400,
        });
      });
    },
    closeContent() {
      this.open = false;
      const el = this.$refs["content-part"];
      if (!el) {
        this.contentIsOpened = false;
        return;
      }
      this.stopAnimation();
      this.animation = this.$anime({
        targets: el,
        maxHeight: [el.offsetHeight, 0],
        easing: "easeOutQuart",
        duration: 400,
        complete: () => {
          // a click during the close animation reopens it -- don't unmount then
          if (!this.open) this.contentIsOpened = false;
        },
      });
    },
    stopAnimation() {
      if (this.animation) {
        this.animation.pause();
        this.animation = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$animation-speed: 0.4s;
// a class, not an id -- several accordions can appear on one page
.accordion {
  .header-part {
    cursor: pointer;

    .title,
    .toggle-icon {
      cursor: pointer;
    }

    // flex, not inline flow: an inline-block icon sits on the text baseline, so
    // the wrapper grew to the line box (38px around a 32px icon) and the icon
    // read as sitting above centre
    .toggle-icon {
      display: flex;
      align-items: center;
    }

    .title,
    .toggle-icon {
      .toggle-arrow {
        display: block;
        width: var(--icon-width);
        height: var(--icon-height);
        background: var(--icon-color);
        transition: $transition-global;
        cursor: pointer;
        mask-image: var(--icon-mask);
        mask-repeat: no-repeat;
        mask-size: contain;

        &.open {
          transform: rotate(180deg);
        }
      }
    }
  }
  .content-part {
    // transition: all 0.4s ease-in-out;
    overflow: hidden;
    max-height: 0;
  }
}
</style>
